import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { Grid, Screen } from '@casa/model';
import { ScreenActiveContext, cssColor } from '@casa/tiles';
import { ScreenView, type ScreenEditing } from './screen_view.tsx';

/** The legacy screen change: 200 ms, linear, the new screen in from the right, the old one out to the left. */
const SLIDE = { duration: 200, easing: 'linear' } as const;

/**
 * Every screen of the dashboard, as the legacy `MainPage` kept them: all mounted, so their data
 * stays current, and only the shown one on the page. Screens that are not shown wait to the right
 * of it, hidden.
 */
export interface ScreenDeckProps {
    screens: readonly Screen[];
    grid: Grid;
    current: number;
    /** Behind every screen's tiles (DS-3); the page's black where unset. */
    background?: string | undefined;
    /** Edit mode, on the shown screen. */
    editing?: ScreenEditing | undefined;
}

export function ScreenDeck({ screens, grid, current, background, editing }: ScreenDeckProps) : ReactNode {
    const elements = useRef<(HTMLDivElement | null)[]>([]);
    const previous = useRef(current);
    const [leaving, setLeaving] = useState<number | null>(null);

    useLayoutEffect(() => {
        const from = previous.current;
        previous.current = current;
        if (from === current) {
            return;
        }

        elements.current[current]?.animate([{ transform: 'translateX(100%)' }, { transform: 'translateX(0)' }], SLIDE);
        const outgoing = elements.current[from]?.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(-100%)' }], SLIDE);
        if (!outgoing) {
            return;
        }

        setLeaving(from);
        outgoing.onfinish = () => setLeaving((index) => (index === from ? null : index));
    }, [current]);

    return (
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', ...(background ? { background: cssColor(background) } : {}) }}>
            {screens.map((screen, index) => {
                const shown = index === current || index === leaving;

                return (
                    <div
                        key={index}
                        ref={(element) => {
                            elements.current[index] = element;
                        }}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            transform: index === current ? 'none' : 'translateX(100%)',
                            visibility: shown ? 'visible' : 'hidden',
                        }}
                    >
                        <ScreenActiveContext.Provider value={shown}>
                            <ScreenView screen={screen} grid={grid} editing={index === current ? editing : undefined} />
                        </ScreenActiveContext.Provider>
                    </div>
                );
            })}
        </div>
    );
}
